import {
  collection,
  collectionGroup,
  doc,
  onSnapshot,
  query,
  where,
  type DocumentChange,
  type Unsubscribe,
} from 'firebase/firestore';

import {
  mapFirestoreDocToListRow,
  parseApplicationDocPath,
  type FirestoreApplicationDocData,
} from '@/lib/applicationFirestoreMapper';
import {
  setApplicationLiveSyncHealth,
  type ApplicationLiveSyncHealth,
} from '@/lib/applicationLiveSyncFlags';
import { mergeApplicationInterviews } from '@/lib/companyApplicationsList';
import { mapPipelineRunsToInterviews } from '@/lib/pipelineRunInterviewMapper';
import { loadBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import type { ApplicationListSync } from '@/lib/applicationListSync';
import { db } from '@/lib/firebase';
import type { Application } from '@/types';

import {
  getMergedWatchRefs,
  setBulkWatchRefs,
  subscribeApplicationWatchSet,
  watchSetKey,
  type ApplicationWatchRef,
} from './applicationWatchSet';

type DocListenerEntry = {
  ref: ApplicationWatchRef;
  unsubApp: Unsubscribe;
  unsubRuns: Unsubscribe | null;
};

let activeCompanyId: string | null = null;
let listSync: ApplicationListSync | null = null;
let unsubPipelineQuery: Unsubscribe | null = null;
let unsubWatchSet: Unsubscribe | null = null;
const docListeners = new Map<string, DocListenerEntry>();
const lastAppDocByKey = new Map<string, FirestoreApplicationDocData>();

function setHealth(next: ApplicationLiveSyncHealth): void {
  setApplicationLiveSyncHealth(next);
}

function preserveCachedInterviews(companyId: string, row: Application): Application {
  const existing = listSync
    ?.getFlattenedListApplications(companyId, false)
    .find((a) => a.id === row.id);
  if (!existing?.interviews?.length) return row;
  const interviews = mergeApplicationInterviews(row.interviews, existing.interviews);
  if (!interviews?.length) return row;
  return {
    ...row,
    interviews,
    hasScheduledInterview: interviews.some((i) => i.status !== 'Cancelled') || undefined,
  };
}

function patchRow(
  companyId: string,
  row: Application,
  options?: { preserveInterviews?: boolean },
): void {
  let next = row;
  if (options?.preserveInterviews || row.interviews.length === 0) {
    next = preserveCachedInterviews(companyId, row);
  }
  listSync?.patchMany(companyId, [next], next.archived === true);
}

function mergeInterviewPatch(
  companyId: string,
  pathKey: string,
  interviews: Application['interviews'],
): void {
  const appDoc = lastAppDocByKey.get(pathKey);
  const entry = docListeners.get(pathKey);
  if (!appDoc || !entry) return;

  const parsed = {
    companyId,
    positionId: entry.ref.positionId,
    applicationId: entry.ref.applicationId,
  };

  const base = mapFirestoreDocToListRow(appDoc, parsed);
  if (!base) return;

  patchRow(companyId, {
    ...base,
    interviews,
    hasScheduledInterview: interviews.some((i) => i.status !== 'Cancelled') || undefined,
  });
}

function handleApplicationSnapshot(
  companyId: string,
  snapshotPath: string,
  data: FirestoreApplicationDocData,
): void {
  const parsed = parseApplicationDocPath(snapshotPath);
  if (!parsed || parsed.companyId !== companyId) return;

  const pathKey = watchSetKey(parsed);
  lastAppDocByKey.set(pathKey, data);

  const row = mapFirestoreDocToListRow(data, parsed);
  if (!row) return;

  const entry = docListeners.get(pathKey);
  patchRow(companyId, row, { preserveInterviews: entry?.unsubRuns != null });
}

function handlePipelineQueryChanges(
  companyId: string,
  changes: DocumentChange<FirestoreApplicationDocData>[],
): void {
  for (const change of changes) {
    const data = change.doc.data();
    const path = change.doc.ref.path;
    if (change.type === 'removed') {
      const parsed = parseApplicationDocPath(path);
      if (!parsed) continue;
      const row = mapFirestoreDocToListRow(data, parsed);
      if (row) patchRow(companyId, row, { preserveInterviews: true });
      continue;
    }
    handleApplicationSnapshot(companyId, path, data);
  }
}

function startInterviewListener(
  companyId: string,
  pathKey: string,
  ref: ApplicationWatchRef,
): Unsubscribe {
  const runsRef = collection(
    db,
    'companies',
    companyId,
    'jobPositions',
    ref.positionId,
    'jobApplications',
    ref.applicationId,
    'pipelineRuns',
  );
  const runsQuery = query(runsRef, where('pipelineKey', '==', 'interview'));

  return onSnapshot(
    runsQuery,
    (snap) => {
      const runs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const appDoc = lastAppDocByKey.get(pathKey);
      const interviews = mapPipelineRunsToInterviews(
        runs,
        ref.applicationId,
        appDoc,
      );
      mergeInterviewPatch(companyId, pathKey, interviews);
    },
    () => {
      setHealth('degraded');
    },
  );
}

function ensureDocListener(companyId: string, ref: ApplicationWatchRef): void {
  const key = watchSetKey(ref);
  if (docListeners.has(key)) return;

  const appRef = doc(
    db,
    'companies',
    companyId,
    'jobPositions',
    ref.positionId,
    'jobApplications',
    ref.applicationId,
  );

  const unsubApp = onSnapshot(
    appRef,
    (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as FirestoreApplicationDocData;
      handleApplicationSnapshot(companyId, snap.ref.path, data);

      const entry = docListeners.get(key);
      const wantsInterview =
        data['hasScheduledInterview'] === true
        || entry?.unsubRuns != null;

      if (wantsInterview && entry && !entry.unsubRuns) {
        const unsubRuns = startInterviewListener(companyId, key, ref);
        docListeners.set(key, { ...entry, unsubRuns });
      }
    },
    () => setHealth('degraded'),
  );

  docListeners.set(key, { ref, unsubApp, unsubRuns: null });
}

function stopDocListener(key: string): void {
  const entry = docListeners.get(key);
  if (!entry) return;
  entry.unsubApp();
  entry.unsubRuns?.();
  docListeners.delete(key);
  lastAppDocByKey.delete(key);
}

function reconcileDocListeners(companyId: string): void {
  const watchRefs = getMergedWatchRefs();
  const desired = new Set(watchRefs.map(watchSetKey));

  for (const ref of watchRefs) {
    ensureDocListener(companyId, ref);
  }

  for (const key of [...docListeners.keys()]) {
    if (!desired.has(key)) stopDocListener(key);
  }
}

function startPipelineRunningQuery(companyId: string): void {
  const q = query(
    collectionGroup(db, 'jobApplications'),
    where('companyId', '==', companyId),
    where('state', '==', 'PIPELINE_RUNNING'),
  );

  unsubPipelineQuery = onSnapshot(
    q,
    (snap) => handlePipelineQueryChanges(companyId, snap.docChanges()),
    () => setHealth('degraded'),
  );
}

function onWatchSetChanged(): void {
  if (!activeCompanyId) return;
  reconcileDocListeners(activeCompanyId);
}

export function refreshBulkWatchEntries(companyId: string): void {
  const loaded = loadBulkProcessQueue(companyId);
  setBulkWatchRefs(
    loaded.targetIds
      .map((applicationId) => ({
        applicationId,
        positionId: loaded.targetRefs?.[applicationId] ?? '',
      }))
      .filter((r) => r.positionId),
  );
}

export function startApplicationLiveSync(
  companyId: string,
  sync: ApplicationListSync,
): void {
  if (activeCompanyId === companyId && listSync === sync) {
    reconcileDocListeners(companyId);
    return;
  }

  stopApplicationLiveSync();

  activeCompanyId = companyId;
  listSync = sync;
  setHealth('connected');

  refreshBulkWatchEntries(companyId);
  startPipelineRunningQuery(companyId);
  unsubWatchSet = subscribeApplicationWatchSet(onWatchSetChanged);
  reconcileDocListeners(companyId);
}

export function stopApplicationLiveSync(): void {
  unsubPipelineQuery?.();
  unsubPipelineQuery = null;
  unsubWatchSet?.();
  unsubWatchSet = null;

  for (const key of [...docListeners.keys()]) {
    stopDocListener(key);
  }

  activeCompanyId = null;
  listSync = null;
  setHealth('idle');
}
